import type { UnitInfoInterface } from 'store/units/types';

export interface DisplayNotes {
  noteOne: boolean;
  noteTwo: boolean;
  noteThree: boolean;
  noteFour: boolean;
  noteFive: boolean;
}

export interface DisplayConfig {
  displayNotes: DisplayNotes;
  displayUnitName: boolean;
  displayModelName: boolean;
  [key: string]: boolean | DisplayNotes;
}

export interface BaseUnitInfo {
  modelName: string;
  parallelUnits: number;
  moisture?: number;
  newMoisture: boolean;
  display: DisplayConfig;
}

export interface UnitConfigParams {
  data?: Record<string, unknown>;
  variantId?: number;
  unitType?: string;
}

export abstract class BaseUnitConfig {
  static getDefaultData(_params?: UnitConfigParams): UnitInfoInterface {
    throw new Error('Must implement getDefaultData in subclass');
  }

  static getNumberOfPorts(_model?: string, _config?: number): number {
    throw new Error('Must implement getNumberOfPorts in subclass');
  }

  static getDefaultDisplay(
    unitSpecificFlags: Partial<DisplayConfig> = {},
  ): DisplayConfig {
    return {
      displayNotes: {
        noteOne: false,
        noteTwo: false,
        noteThree: false,
        noteFour: false,
        noteFive: false,
      },
      displayUnitName: true,
      displayModelName: true,
      ...unitSpecificFlags,
    };
  }

  static getBaseDefaults(): {
    parallelUnits: number;
    moisture: number;
    newMoisture: boolean;
  } {
    return {
      parallelUnits: 1,
      moisture: 0,
      newMoisture: false,
    };
  }

  getDefaultDisplay(
    unitSpecificFlags: Partial<DisplayConfig> = {},
  ): DisplayConfig {
    return (this.constructor as typeof BaseUnitConfig).getDefaultDisplay(
      unitSpecificFlags,
    );
  }

  getBaseDefaults(): {
    parallelUnits: number;
    moisture: number;
    newMoisture: boolean;
  } {
    return (this.constructor as typeof BaseUnitConfig).getBaseDefaults();
  }

  /**
   * Instance method implementations that subclasses can override.
   * These allow calling methods on instances during Phase 1-2 transition.
   * Default implementations delegate to static methods.
   */
  getDefaultData(params?: UnitConfigParams): UnitInfoInterface {
    return (this.constructor as typeof BaseUnitConfig).getDefaultData(params);
  }

  getNumberOfPorts(model?: string, config?: number): number {
    return (this.constructor as typeof BaseUnitConfig).getNumberOfPorts(
      model,
      config,
    );
  }
}
